/**
 * 나타나고 사라지는 느낌.
 *
 * `interaction.ts`가 **누르는** 순간을 다룬다면 여기는 **없던 것이 생기고
 * 있던 것이 없어지는** 순간을 다룬다. 둘은 요구가 반대다. 누르는 느낌은
 * 즉각이어야 하고(120ms 넘기지 않는다), 등장은 눈이 따라갈 시간이 있어야 한다.
 * 즉각 나타나면 어디서 왔는지 못 보고 화면이 통째로 갈아끼워진 것처럼 읽힌다.
 *
 * 시간과 거리 값 자체는 `index.css`의 `@theme`에 있고, 여기서는 **어디에 무엇을
 * 붙이는지**만 정한다. 컴포넌트마다 따로 적으면 어떤 모달은 떠오르고 어떤
 * 모달은 툭 나타나는 상태가 된다 — 지금까지가 그랬다.
 *
 * 움직임을 원하지 않는 사람에게는 `index.css`의 `prefers-reduced-motion`이
 * 전역으로 꺼준다. 여기서 따로 분기하지 않는다.
 */

/**
 * 모달 뒤를 덮는 판. 배경이 갑자기 어두워지면 화면이 깜빡인 것처럼 보인다.
 *
 * Radix가 `data-state`을 붙여주고, **닫힐 때는 애니메이션이 끝날 때까지
 * 기다렸다가** DOM에서 뺀다. 그래서 사라지는 것도 보인다.
 */
export const OVERLAY_MOTION =
  "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out";

/** 모달 본체. 살짝 커지며 떠오른다. */
export const DIALOG_MOTION =
  "data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out";

/**
 * 팝오버. 누른 자리에서 자라나야 어디서 나온 것인지 보인다.
 *
 * `origin-*`은 Radix가 트리거 위치를 재서 넣어주는 값이라 런타임에 정해진다 —
 * tailwind-rules §6①이 말하는, CSS 변수를 경유하는 경우다.
 */
export const POPOVER_MOTION =
  "origin-[var(--radix-popover-content-transform-origin)] data-[state=open]:animate-popover-in data-[state=closed]:animate-popover-out";

/** 화면 아래에 붙는 것 — 스낵바처럼 밑에서 올라오는 게 자연스러운 것. */
export const RISE_IN = "animate-rise-in";

/** 방금 도착한 말풍선 하나. 방을 열 때 있던 것에는 붙이지 않는다. */
export const BUBBLE_IN = "animate-bubble-in";

/**
 * 내용이 통째로 갈릴 때 — 목록 탭을 옮기거나 빈 화면이 바뀔 때.
 * 자리를 옮기지 않고 밝기만 든다. 자리까지 움직이면 읽던 줄을 놓친다.
 */
export const ENTER = "animate-enter";

/**
 * 접혀 있던 자리에서 펼쳐지는 것 — 선택지 목록처럼 **뒤 내용을 밀어내며** 생기는 것.
 *
 * `RISE_IN`과 방향이 반대다. 저건 화면 밑에서 떠오르고, 이건 위에서 내려온다.
 * 밀려나는 쪽과 같은 방향으로 움직여야 "펼쳐졌다"로 읽힌다.
 */
export const DROP_IN = "animate-drop-in";
